<template>
  <section v-if="appHasOrders">
    <label><input type="checkbox" v-model="showOrder" @click="checkOrder" /> Order</label>
    <section label="Order" v-if="showOrder">
      <page-toolbar class="toolbar-dark">
        <kendo-button
          icon="add"
          title="Add Order"
          v-if="editable && !hasOrder"
          @click="addOrderClick"
        >
        </kendo-button>
        <kendo-button
          icon="cancel"
          title="Cancel Changes"
          v-if="editable"
          @click="cancelGridChanges"
        >
        </kendo-button>
        <kendo-button
          icon="copy"
          title="Duplicate"
          v-if="editable && orderIsDone"
          @click="openRangeDealsDialog"
        >
        </kendo-button>
      </page-toolbar>
      <kendo-grid
        style="width:100%;font-size:82%"
        :data-source="dataSource"
        :columns="columns"
        :editable="editableSettings"
        :navigatable="true"
        id="dealOrderGrid"
      />
      <range-deals
        v-model="rangeDealsDialog"
        @datesSelected="onRangeDatesSelected"
        :initial-start-date="tradeDate"
      />
    </section>
  </section>
</template>
<script>
import axios from 'axios'
import PageToolbar from '@/components/PageToolbar'
import {mapGetters} from 'vuex'
import {
  checkboxColumn,
  jsonDropDownEditor,
  numericEditor,
  readOnlyEditor,
  dateTimePickerEditor,
} from '@/helpers/kendo-editors'
import {isBuyingEditor, baseCcyEditor} from './editors'
import RangeDeals from './RangeDeals'
import {datePickerEditor} from '@/helpers/kendo-editors'
import {product} from '@/helpers/enums'

export default {
  inject: ['EventBus'],
  props: {
    marketId: {type: Number, required: true},
    order: {type: Object, required: false, default: () => null},
    defaultDealOptions: {type: Object, required: true},
    editable: {type: Boolean, required: true},
    tradeDate: {type: [String, Date], required: true},
    dealTypeId: {type: Number, required: true},
    dealModel: {type: Object, required: true},
  },
  async mounted() {
    this.allCcies = (await axios.get(
      '/api/list/deal/getAllCurrencies?clientId=' + this.clientId + '&marketId=' + this.marketId,
    )).data
  },
  watch: {
    'dealModel.productId': {
      immediate: true,
      async handler(newValue) {
        if (this.workOrder) {
          const leg = this.getLegByProduct()
          this.workOrder.legId = leg.legId
          this.workOrder.legCode = leg.legCode
        }

        if (!newValue || this.dealModel.dealTypeId != 3) return
        this.productDetail = await this.getProductDetail(newValue)
      },
    },
    order(newValue) {
      this.workOrder = {...newValue}
    },
    showOrder(newValue) {
      if (newValue) {
        this.$emit('clearDeals')
      }

      this.$emit('showHideOrder', newValue)
    },
  },
  data() {
    return {
      workOrder: this.order != null ? {...this.order} : {},
      showAllColumns: true,
      hasOrder: this.order != null,
      rangeDealsDialog: false,
      showOrder: this.order != null,
      allCcies: [],
      productDetail: {},
    }
  },
  methods: {
    addOrderClick() {
      this.dataSource.add()
      this.hasOrder = true
    },
    openRangeDealsDialog() {
      this.rangeDealsDialog = true
    },
    cancelGridChanges() {
      this.hasOrder = this.order != null
      this.dataSource.cancelChanges()
    },
    isEmpty() {
      return this.dataSource.data().length == 0
    },
    async sync() {
      await this.dataSource.sync()
      const [first] = this.dataSource.data()
      if (first) {
        this.workOrder = {...first.toJSON()}
      } else {
        this.workOrder = null
      }

      this.hasOrder = this.workOrder != null

      this.$emit('updateOrder', this.workOrder)
    },
    onRangeDatesSelected(dates) {
      this.EventBus.$emit('addOperationsFromOrder', {dates, order: this.order})
    },
    checkOrder(e) {
      if (e.target.checked) {
        setTimeout(() => this.addOrderClick(), 0)
      }
    },
    async getProductDetail(productId) {
      const {data: productDetail} = await axios.get('/api/list/Future/getFutureProductDetail', {
        params: {productId, clientId: this.clientId},
      })

      return productDetail
    },
    getLegByProduct() {
      let base = []
      let productId = this.dealModel.productId * 1
      switch (productId) {
        case product.Fwd:
        case product.Avg:
        case product.Window:
        case product.Future:
          base.legCode = 'FWD'
          base.legId = 1
          return base
        case product.Collar:
          base.legCode = 'PUT'
          base.legId = 4
          return base
        case product.Spot:
          base.legCode = 'SPOT'
          base.legId = 2
          return base
        case product.Exotic:
          base.legCode = 'CALL'
          base.legId = 3
          return base
        case product.FxSwap:
          base.legCode = 'SPOT'
          base.legId = 2
          return base
        case product.Call:
          base.legCode = 'CALL'
          base.legId = 3
          return base
        case product.Put:
          base.legCode = 'PUT'
          base.legId = 4
          return base
        default: {
          const order = this.dealModel.order
          base.legCode = order ? order.legCode : 'FWD'
          base.legId = order ? order.legId : 1
          return base
        }
      }
    },
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId', appHasOrders: 'app/hasOrders'}),
    dataSource() {
      return new kendo.data.DataSource({
        data: this.order ? [this.workOrder] : [],
        schema: {
          model: {
            id: 'orderId',
            fields: {
              orderId: {type: 'number', defaultValue: 0, editable: false},
              statusName: {type: 'string', defaultValue: 'Active'},
              statusId: {type: 'number', defaultValue: 1},
              orderDateTime: {
                type: 'date',
                defaultValue: () => {
                  return kendo.parseDate(this.tradeDate)
                },
              },
              contractMonth: {type: 'date'},
              legCode: {
                type: 'string',
                defaultValue: () => {
                  return this.getLegByProduct().legCode
                },
              },
              legId: {
                type: 'number',
                defaultValue: () => {
                  return this.getLegByProduct().legId
                },
              },
              isBuying: {type: 'boolean', defaultValue: this.defaultDealOptions.isBuying},
              numberContract: {type: 'number', defaultValue: 0},
              amount: {type: 'number', defaultValue: 0},
              ccyPairId: {type: 'number', defaultValue: this.defaultDealOptions.ccyPairId},
              ccy1: {type: 'string', defaultValue: this.defaultDealOptions.foreignCcy},
              ccy2: {type: 'string', defaultValue: this.defaultDealOptions.baseCcy},
              orderRate: {type: 'number', defaultValue: 0},
              orderTypeName: {type: 'string', defaultValue: 'Limit'},
              orderTypeId: {type: 'number', defaultValue: 1},
              gtcDate: {type: 'date', defaultValue: null},
              ocoRate: {type: 'number', defaultValue: 0},
              executedDate: {type: 'date', defaultValue: null},
              isInputReuter: {type: 'boolean', defaultValue: false},
              isCancelledReuter: {type: 'boolean', defaultValue: false},
              alarm: {type: 'boolean', defaultValue: true},
              comment: {type: 'string'},
            },
          },
        },
        change: function({action, field, items}) {
          if (action == 'add' || action == 'remove') {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.hasOrder = action == 'add'
            return
          }

          if (action != 'itemchange') return

          const [item] = items
          switch (field) {
            case 'ccy1':
              {
                item.set('ccy2', '')

                const foundCcy = this.allCcies.find(ccy => ccy.foreignCcy == item.get('ccy1'))
                if (foundCcy) {
                  item.set('ccy2', foundCcy.baseCcy)
                  item.set('ccyPairId', foundCcy.ccyPairId)
                }
              }
              break
            case 'numberContract':
              item.set('amount', (item.numberContract || 0) * this.productDetail.contractSize)
              break
          }
        }.bind(this),
      })
    },
    columns() {
      const alignRight = {
        attributes: {style: 'text-align: right;'},
      }
      const alignCenter = {
        attributes: {style: 'text-align: center;'},
      }
      const rateColumnSettings = {
        editor: numericEditor({
          decimals: 16,
          format: '{0:n4}',
          spinners: false,
          step: 0,
        }),
        ...alignRight,
      }

      const columns = [
        {
          field: 'statusName',
          title: 'Status',
          width: 90,
          editor: jsonDropDownEditor('statusId', 'statusName', '/api/list/Order/getOrderStatuses'),
        },
        {
          field: 'orderDateTime',
          title: 'Date/Time',
          format: '{0:yyyy-MM-dd HH:mm}',
          width: 100,
          editor: dateTimePickerEditor({format: 'yyyy-MM-dd HH:mm', min: this.tradeDate}, true),
          ...alignCenter,
        },
        {
          field: 'legCode',
          title: 'Leg',
          width: 90,
          editor: jsonDropDownEditor(
            'legId',
            'legCode',
            '/api/list/deal/getLegs?dealTypeId=' + this.dealTypeId,
            {
              autoWidth: true,
              dataBound: function() {
                this.trigger('change')
              },
            },
            false,
          ),
        },
        {
          field: 'contractMonth',
          title: 'Contract Month',
          width: 100,
          format: '{0:yyyy-MM}',
          attributes: {style: 'text-align: right;'},
          editor: datePickerEditor({format: 'yyyy-MM', start: 'year', depth: 'year'}),
          hidden: this.dealTypeId != 3,
        },
        {
          field: 'isBuying',
          title: 'Buy / Sell',
          width: 80,
          editor: isBuyingEditor,
          template: function(dataItem) {
            return '<span>' + (dataItem.isBuying ? 'Buy' : 'Sell') + '</span>'
          },
        },
        {
          field: 'numberContract',
          title: 'Number of Contract',
          width: 100,
          format: '{0:yyyy-MM}',
          attributes: {style: 'text-align: right;'},
          editor: numericEditor({
            decimals: 2,
            format: '{0:n0}',
            spinners: false,
            step: 0,
          }),
          hidden: this.dealTypeId != 3,
        },
        {
          field: 'amount',
          title: 'Amount',
          format: '{0:N0}',
          width: 110,
          editor:
            this.dealTypeId == 3
              ? readOnlyEditor
              : numericEditor({
                  decimals: 2,
                  format: '{0:n0}',
                  spinners: false,
                  step: 0,
                }),
          ...alignRight,
        },
        {
          field: 'ccy1',
          title: 'Com.Ccy',
          width: 80,
          editor:
            this.dealTypeId == 3
              ? readOnlyEditor
              : jsonDropDownEditor('foreignCcy', 'foreignCcy', '', {
                  dataSource: {
                    transport: {
                      read: {
                        url: '/api/list/deal/getForeignCurrencies',
                        data: () => ({
                          clientId: this.clientId,
                          marketId: this.marketId,
                        }),
                      },
                    },
                  },
                }),
        },
        {
          field: 'ccy2',
          title: 'Ccy',
          width: 80,
          editor: (function(vm) {
            return function(container, options) {
              return baseCcyEditor(vm.clientId, vm.marketId).call(this, container, options)
            }
          })(this),
        },
        {
          field: 'orderRate',
          title: 'Order Rate',
          width: 90,
          ...rateColumnSettings,
        },
        {
          field: 'orderTypeName',
          title: 'Order Type',
          width: 110,
          editor: jsonDropDownEditor(
            'orderTypeId',
            'orderTypeName',
            '/api/list/Order/getOrderTypes',
          ),
        },
        {
          field: 'gtcDate',
          title: 'Gtc Date',
          width: 100,
          format: '{0:yyyy-MM-dd}',
          editor: datePickerEditor({format: 'yyyy-MM-dd', min: this.tradeDate}),
        },
        {
          field: 'ocoRate',
          title: 'Oco Rate',
          hideByDefault: true,
          width: 90,
          ...rateColumnSettings,
        },
        {
          field: 'executedDate',
          title: 'Executed Date',
          hideByDefault: true,
          width: 90,
          format: '{0:yyyy-MM-dd}',
          editor: datePickerEditor({format: 'yyyy-MM-dd', min: this.tradeDate}),
        },
        checkboxColumn(
          'isInputReuter',
          {title: 'Input Reuter', width: 80, hidden: true},
          false,
          true,
        ),
        checkboxColumn(
          'isCancelledReuter',
          {title: 'Cancelled Reuter', width: 80, hidden: true},
          false,
          true,
        ),
        checkboxColumn('alarm', {title: 'Alarm', width: 80, hideByDefault: true}, false, true),
        {field: 'comment', title: 'Comment', hideByDefault: true, width: 150},
        {
          command: 'destroy',
          title: '&nbsp;',
          width: 64,
          hidden: !this.editable,
        },
        {},
      ]

      return this.showAllColumns ? columns : columns.filter(c => !c.hideByDefault)
    },
    orderIsDone() {
      return this.order && this.order.statusId == 3
    },
    editableSettings: function() {
      return {
        mode: 'incell',
        update: this.editable,
        destroy: this.editable,
        confirmation: false,
        createAt: 'bottom',
      }
    },
  },
  components: {
    PageToolbar,
    RangeDeals,
  },
}
</script>
